import { ChannelDestination } from 'communication/Channel';
import { LoggingService } from '@instinet-analytics/core-lib';
import { PortfolioService } from 'portfolio/PortfolioService';
import StreamDataService from 'streamsState/StreamDataService';
import { WidgetId } from 'widgets/WidgetId';
import { WidgetsService } from 'widgets/WidgetsService';
import { ActionsDispatcher } from 'store/ActionsDispatcher';
import { IndigoStore } from 'store/store';
import State from 'store/State';
import IndigoApi from 'communication/IndigoApi';
import RequestProgressService from 'requestProgress/RequestProgressService';
import IndigoServiceClient from 'communication/IndigoServiceClient';
import IndigoApiError from 'communication/IndigoApiError';
import { MarketDataRequest } from 'marketData/MarketDataRequest';
import { WidgetMarketDataRequestManager } from 'marketData/WidgetMarketDataRequestManager';
import { DataServices } from 'store/Services';
import { WidgetStrategyWithStreamByRequestUid } from 'widgets/WidgetRegistry';

export type StreamingEndpointId = {
    type: 'stream';
    channelDestination: ChannelDestination;
};
export type RestEndpointId = {
    type: 'rest';
    endpointUid: string;
};
export type EndpointId = StreamingEndpointId | RestEndpointId;

interface WidgetCustomManagedSharedOptions {
    dataServices: DataServices;
    loggingService: LoggingService;
    portfolioService: PortfolioService;
    streamDataService: StreamDataService;
    widgetsService: WidgetsService;
    store: IndigoStore;
    indigoApi: IndigoApi;
    requestProgressService: RequestProgressService;
    marketDataRequestManager: WidgetMarketDataRequestManager;
}
export interface WidgetCustomManagedStreamOptions
    extends WidgetCustomManagedSharedOptions {
    channelDestination: ChannelDestination;
}
export interface WidgetCustomManagedRestOptions
    extends WidgetCustomManagedSharedOptions {
    indigoServiceClient: IndigoServiceClient;
    dataServices: DataServices;
    endpointUid: string;
    callApi: (
        dispatcher: ActionsDispatcher,
        requestProgressService: RequestProgressService,
        indigoServiceClient: IndigoServiceClient,
        dataServices: DataServices,
        payload: unknown
    ) => Promise<unknown>;
    clearRequestData: (dispatcher: ActionsDispatcher, key: string) => void;
    handleMessage: (
        dispatcher: ActionsDispatcher,
        key: string,
        request: unknown,
        response: unknown
    ) => void;
    handleError: (
        dispatcher: ActionsDispatcher,
        key: string,
        request: unknown,
        error: IndigoApiError
    ) => void;
    removeRequestData: (dispatcher: ActionsDispatcher, key: string) => void;
}

export type WidgetCustomManagedOptions =
    | WidgetCustomManagedStreamOptions
    | WidgetCustomManagedRestOptions;
/*
 * Config for a widget that uses a stream with a custom stream manager
 */
export interface WidgetCustomManagedStreamConfig {
    channelDestination: ChannelDestination;
    managerFactory: (
        options: WidgetCustomManagedStreamOptions
    ) => WidgetCustomStreamManager;
}
/*
 * Config for a widget that uses a rest endpoint with a custom stream manager
 */
export interface WidgetCustomManagedRestConfig<TRequest, TResponse> {
    removeRequestData: (dispatcher: ActionsDispatcher, key: string) => void;
    clearRequestData: (dispatcher: ActionsDispatcher, key: string) => void;
    handleError: (
        dispatcher: ActionsDispatcher,
        key: string,
        request: TRequest,
        error: IndigoApiError
    ) => void;
    handleMessage: (
        dispatcher: ActionsDispatcher,
        key: string,
        request: TRequest,
        response: TResponse
    ) => void;
    callApi: (
        dispatcher: ActionsDispatcher,
        requestProgressService: RequestProgressService,
        indigoServiceClient: IndigoServiceClient,
        dataServices: DataServices,
        payload: TRequest
    ) => Promise<TResponse>;
    apiUid: string;
    managerFactory: (
        options: WidgetCustomManagedRestOptions
    ) => WidgetCustomStreamManager;
}

export type WidgetCustomManagedConfig<TPayload> =
    | WidgetCustomManagedStreamConfig
    | WidgetCustomManagedRestConfig<TPayload, unknown>;

export function isWidgetCustomManagedStreamConfig(
    config: WidgetCustomManagedConfig<unknown>
): config is WidgetCustomManagedStreamConfig {
    return (
        config &&
        !!(config as WidgetCustomManagedStreamConfig).channelDestination
    );
}
export interface StreamRequestInfo<T> {
    /*
     * The unique key for the stream that will be used for this request
     */
    streamKey: string;
    /*
     * The UI for the payload, to allow streams to be re-used for many payloads
     */
    payloadUid?: string;
    payload: T;
}

interface ByRequestUidConfig<TPayload> {
    getWidgetStreamRequestInfo: (
        state: State,
        services: DataServices,
        id: WidgetId,
        activeInputUsesOrdersStream: boolean
    ) => Promise<
        StreamRequestInfo<TPayload>[] | StreamRequestInfo<TPayload> | undefined
    >;
    getStreamRequestInfosForSymbols(
        state: State,
        services: DataServices,
        symbols: string[],
        config: unknown,
        quantities?: number[]
    ): Promise<StreamRequestInfo<TPayload>[]>;
}

export type WidgetStreamByRequestUidConfig<TPayload> =
    WidgetCustomManagedStreamConfig & ByRequestUidConfig<TPayload>;

export type WidgetRestByRequestUidConfig<TRequest, TResponse> =
    WidgetCustomManagedRestConfig<TRequest, TResponse> &
        ByRequestUidConfig<TRequest>;

export type WidgetByRequestUidConfig<TPayload> =
    | WidgetStreamByRequestUidConfig<TPayload>
    | WidgetRestByRequestUidConfig<TPayload, unknown>;

interface MarketDataRequestConfig {
    getMarketDataRequests: (
        state: State,
        services: DataServices,
        id: WidgetId,
        activeInputUsesOrdersStream: boolean
    ) => Promise<MarketDataRequest[]>;
    getMarketDataRequestForSymbols(
        state: State,
        services: DataServices,
        symbols: string[],
        config: unknown
    ): Promise<MarketDataRequest[]>;
}

export type WidgetWithMarketDataRequestConfig = (
    | WidgetCustomManagedStreamConfig
    | WidgetStrategyWithStreamByRequestUid<unknown>
) &
    MarketDataRequestConfig;

export function isWidgetByRequestUidConfig(
    config: WidgetCustomManagedConfig<unknown>
): config is WidgetByRequestUidConfig<unknown> {
    return (
        config &&
        !!(config as WidgetByRequestUidConfig<unknown>)
            .getWidgetStreamRequestInfo
    );
}

export function isWidgetWithMarketDataRequestConfig(
    config:
        | WidgetCustomManagedConfig<unknown>
        | WidgetStrategyWithStreamByRequestUid<unknown>
): config is WidgetWithMarketDataRequestConfig {
    return (
        config &&
        !!(config as WidgetWithMarketDataRequestConfig).getMarketDataRequests
    );
}

export interface WidgetCustomStreamManager {
    register(widgetId: WidgetId): { handled: boolean };

    dispose(widgetId: WidgetId): { handled: boolean };

    checkStream(reason: string, dispatcher: ActionsDispatcher): void;

    onAppSettingsChanged(dispatcher: ActionsDispatcher): void;

    onWidgetConfigChanged(
        widgetId: WidgetId,
        dispatcher: ActionsDispatcher
    ): void;

    onWidgetComponentStateChanged(
        widgetId: WidgetId,
        dispatcher: ActionsDispatcher
    ): void;

    onPortfolioSelectionChanged(dispatcher: ActionsDispatcher): void;

    onPortfolioStreamingOrderIdsChanged(dispatcher: ActionsDispatcher): void;

    onFilteredPortfolioChangedAsync(dispatcher: ActionsDispatcher): void;

    getInfo(): Record<string, unknown>;

    resetState(): void;
}

export const nullWidgetCustomStreamManager: WidgetCustomStreamManager = {
    register: (_k) => ({
        handled: false,
    }),
    dispose: (_k) => ({
        handled: false,
    }),
    checkStream: (_r) => {
        return;
    },
    onAppSettingsChanged: () => {
        return;
    },
    onPortfolioSelectionChanged: () => {
        return;
    },
    onPortfolioStreamingOrderIdsChanged: () => {
        return;
    },
    onWidgetComponentStateChanged: (_k) => {
        return;
    },
    onWidgetConfigChanged: (_k) => {
        return;
    },
    onFilteredPortfolioChangedAsync: (_k) => {
        return;
    },
    getInfo(): Record<string, unknown> {
        return { type: 'nullWidgetCustomStreamManager' };
    },
    resetState() {
        return;
    },
};
